import { useEffect, useState } from 'react'
import { ChevronDown, SlidersHorizontal } from 'lucide-react'
import { cn } from '@/lib/utils'
import type { AgenticDesignSystemTab } from './types'
import { useStudioLocale } from '@/i18n'
import { getDesignDirectionCopy } from './design-direction-copy'

interface AgenticTabsProps {
  activeTab: AgenticDesignSystemTab
  onSelect: (tab: AgenticDesignSystemTab) => void
}

const tabs: Array<{ key: AgenticDesignSystemTab; label: string }> = [
  { key: 'systems', label: 'Design systems' },
  { key: 'validation', label: 'Validation Lab' },
  { key: 'vocabulary', label: 'Vocabulary' },
  { key: 'briefings', label: 'Briefings' },
  // English labels above are the fallback identity; the rendered label comes
  // from the dictionary so the tab strip follows the creator's language.
]

export function AgenticTabs({ activeTab, onSelect }: AgenticTabsProps) {
  const { locale, strings } = useStudioLocale()
  const tabCopy = strings.designSystem.tabs
  const directionCopy = getDesignDirectionCopy(locale)
  const [advancedOpen, setAdvancedOpen] = useState(activeTab !== 'systems')

  useEffect(() => {
    if (activeTab !== 'systems') setAdvancedOpen(true)
  }, [activeTab])

  const primaryTab = tabs[0]
  const advancedTabs = tabs.slice(1)
  return (
    <nav
      role="tablist"
      aria-label={strings.a11y.designSystemSurfaces}
      className="flex flex-wrap items-start gap-2 border-b border-border px-4 py-2"
    >
      <button
        type="button"
        role="tab"
        aria-selected={activeTab === primaryTab.key}
        onClick={() => onSelect(primaryTab.key)}
        className={cn(
          'inline-flex min-h-10 items-center gap-2 rounded-lg px-3 text-sm font-medium transition-colors',
          activeTab === primaryTab.key
            ? 'bg-primary/10 text-primary'
            : 'text-muted-foreground hover:bg-muted hover:text-foreground',
        )}
      >
        <SlidersHorizontal className="h-4 w-4" aria-hidden />
        {tabCopy[primaryTab.key] ?? primaryTab.label}
      </button>

      <details
        open={advancedOpen}
        onToggle={(event) => {
          const open = event.currentTarget.open
          setAdvancedOpen(open)
          if (!open && activeTab !== 'systems') onSelect('systems')
        }}
        className="group min-w-0"
        data-testid="design-system-advanced-tabs"
      >
        <summary className="inline-flex min-h-10 cursor-pointer list-none items-center gap-2 rounded-lg px-3 text-sm font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground marker:content-none">
          {directionCopy.advanced}
          <ChevronDown className="h-4 w-4 transition-transform group-open:rotate-180" aria-hidden />
        </summary>
        <div className="mt-2 flex flex-wrap gap-1 rounded-xl border border-border bg-card p-1 shadow-sm">
          {advancedTabs.map((tab) => {
            const active = activeTab === tab.key
            return (
              <button
                key={tab.key}
                type="button"
                role="tab"
                aria-selected={active}
                onClick={() => onSelect(tab.key)}
                className={cn(
                  'whitespace-nowrap rounded-lg px-3 py-2 text-sm font-medium transition-colors',
                  active
                    ? 'bg-primary/10 text-primary'
                    : 'text-muted-foreground hover:bg-muted hover:text-foreground',
                )}
              >
                {tabCopy[tab.key] ?? tab.label}
              </button>
            )
          })}
        </div>
      </details>
    </nav>
  )
}
